import { useState, useCallback, useMemo, useEffect } from 'react';
import { usePowerSyncDb, usePowerSyncQuery } from '../../../adapters/powersync';

// ─── Types ───

export interface TabConfig {
  key: string;
  label: string;
  visible: boolean;
}

export type LayoutStyle = 'tabs' | 'accordion' | 'guided_note';
export type AccordionMode = 'single' | 'multiple';
export type GuidedNotePreset = 'general' | 'dental' | 'aesthetic' | 'custom';

export interface LayoutConfig {
  style: LayoutStyle;
  accordionMode: AccordionMode;
  defaultOpenSection: string | null; // tab key
  rememberLastOpen: boolean;
  guidedNotePreset: GuidedNotePreset;
}

export const DEFAULT_LAYOUT: LayoutConfig = {
  style: 'tabs',
  accordionMode: 'single',
  defaultOpenSection: 'summary',
  rememberLastOpen: false,
  guidedNotePreset: 'general',
};

export interface EncounterTabSettings {
  version: 1;
  preset: string | null;
  tabs: TabConfig[];
  layout?: LayoutConfig;
}

interface EncounterTabSettingsRow {
  id: string;
  org_id: string;
  user_id: string;
  preset: string | null;
  tabs_json: string;
  created_at: string;
  updated_at: string;
}

// ─── Default tabs ───

export const DEFAULT_TABS: TabConfig[] = [
  { key: 'summary', label: 'Summary', visible: true },
  // PRD.233 — Chief Complaint promoted to a first-class section sitting
  // immediately after Summary. `reconcileTabConfig` will append this for
  // existing users whose stored config predates this entry.
  { key: 'chief_complaint', label: 'Chief Complaint', visible: true },
  { key: 'vitals', label: 'Vitals', visible: true },
  { key: 'charting', label: 'Charting', visible: true },
  { key: 'notes', label: 'Notes', visible: true },
  // PRD.214 — Symptoms & Diagnosis structured chip entry
  { key: 'symptoms', label: 'Symptoms', visible: true },
  { key: 'diagnoses', label: 'Diagnosis', visible: true },
  { key: 'prescriptions', label: 'Prescriptions', visible: true },
  { key: 'treatment_plan', label: 'Treatment Plan', visible: true },
  { key: 'forms', label: 'Forms', visible: true },
  { key: 'files', label: 'Files', visible: true },
  { key: 'invoices', label: 'Invoices', visible: true },
  // PRD.201 — Encounter Pharmacy tab. Sits between Invoices and Payments
  // because dispense eligibility is driven by paid invoice lines.
  { key: 'pharmacy', label: 'Pharmacy', visible: true },
  { key: 'payments', label: 'Payments', visible: true },
];

// ─── Presets ───

export const PRESETS: Record<string, { label: string; tabs: TabConfig[] }> = {
  front_desk: {
    label: 'Front Desk',
    tabs: [
      { key: 'summary', label: 'Summary', visible: true },
      // PRD.233 — Front desk may capture the patient's stated complaint
      // at intake, so visible by default.
      { key: 'chief_complaint', label: 'Chief Complaint', visible: true },
      { key: 'vitals', label: 'Vitals', visible: true },
      { key: 'forms', label: 'Forms', visible: true },
      { key: 'files', label: 'Files', visible: true },
      { key: 'invoices', label: 'Invoices', visible: true },
      { key: 'pharmacy', label: 'Pharmacy', visible: true },
      { key: 'payments', label: 'Payments', visible: true },
      { key: 'notes', label: 'Notes', visible: true },
      { key: 'charting', label: 'Charting', visible: false },
      { key: 'prescriptions', label: 'Prescriptions', visible: false },
      { key: 'treatment_plan', label: 'Treatment Plan', visible: false },
    ],
  },
  doctor: {
    label: 'Doctor',
    tabs: [
      { key: 'summary', label: 'Summary', visible: true },
      // PRD.233 — Chief Complaint is core clinical content for doctors.
      { key: 'chief_complaint', label: 'Chief Complaint', visible: true },
      { key: 'vitals', label: 'Vitals', visible: true },
      { key: 'charting', label: 'Charting', visible: true },
      { key: 'files', label: 'Files', visible: true },
      { key: 'notes', label: 'Notes', visible: true },
      { key: 'symptoms', label: 'Symptoms', visible: true }, // PRD.214
      { key: 'diagnoses', label: 'Diagnosis', visible: true }, // PRD.214
      { key: 'treatment_plan', label: 'Treatment Plan', visible: true },
      { key: 'prescriptions', label: 'Prescriptions', visible: true },
      { key: 'forms', label: 'Forms', visible: true },
      { key: 'pharmacy', label: 'Pharmacy', visible: true },
      { key: 'invoices', label: 'Invoices', visible: false },
      { key: 'payments', label: 'Payments', visible: false },
    ],
  },
  nurse: {
    label: 'Nurse',
    tabs: [
      { key: 'summary', label: 'Summary', visible: true },
      // PRD.233 — Nurses commonly capture / triage the complaint.
      { key: 'chief_complaint', label: 'Chief Complaint', visible: true },
      { key: 'vitals', label: 'Vitals', visible: true },
      { key: 'notes', label: 'Notes', visible: true },
      { key: 'forms', label: 'Forms', visible: true },
      { key: 'files', label: 'Files', visible: true },
      { key: 'charting', label: 'Charting', visible: false },
      { key: 'prescriptions', label: 'Prescriptions', visible: false },
      { key: 'treatment_plan', label: 'Treatment Plan', visible: false },
      { key: 'invoices', label: 'Invoices', visible: false },
      { key: 'pharmacy', label: 'Pharmacy', visible: false },
      { key: 'payments', label: 'Payments', visible: false },
    ],
  },
  billing: {
    label: 'Billing',
    tabs: [
      { key: 'summary', label: 'Summary', visible: true },
      { key: 'invoices', label: 'Invoices', visible: true },
      { key: 'pharmacy', label: 'Pharmacy', visible: true },
      { key: 'payments', label: 'Payments', visible: true },
      { key: 'forms', label: 'Forms', visible: true },
      // PRD.233 — Billing role doesn't author chief complaint; hidden by default.
      { key: 'chief_complaint', label: 'Chief Complaint', visible: false },
      { key: 'files', label: 'Files', visible: false },
      { key: 'charting', label: 'Charting', visible: false },
      { key: 'notes', label: 'Notes', visible: false },
      { key: 'prescriptions', label: 'Prescriptions', visible: false },
      { key: 'treatment_plan', label: 'Treatment Plan', visible: false },
      { key: 'vitals', label: 'Vitals', visible: false },
    ],
  },
};

// ─── Reconciliation ───

function reconcileTabConfig(defaults: TabConfig[], stored: TabConfig[]): TabConfig[] {
  const knownKeys = new Set(defaults.map((t) => t.key));
  const storedKeys = new Set(stored.map((t) => t.key));

  // Keep stored tabs that still exist in defaults (preserves user reorder).
  const result = stored.filter((t) => knownKeys.has(t.key));

  // For each new default tab (in DEFAULT_TABS order), insert it at the
  // position implied by its location in `defaults` — splice it right
  // after the previous default tab that already exists in `result`.
  // Without this, freshly-added defaults (e.g. PRD.233's `chief_complaint`,
  // which sits at index 1 right after `summary`) would land at the END
  // of every existing user's tab list. With it, the new tab appears
  // exactly where its position in `DEFAULT_TABS` says it should.
  const seen = new Set(storedKeys);
  for (let i = 0; i < defaults.length; i++) {
    const d = defaults[i];
    if (seen.has(d.key)) continue;
    let insertAt = 0;
    for (let j = i - 1; j >= 0; j--) {
      const prevKey = defaults[j].key;
      const idx = result.findIndex((t) => t.key === prevKey);
      if (idx >= 0) {
        insertAt = idx + 1;
        break;
      }
    }
    result.splice(insertAt, 0, d);
    seen.add(d.key);
  }

  return result;
}

// ─── localStorage cache ───

function cacheKey(orgId: string, userId: string): string {
  return `helios.encounter_tabs.v1.${orgId}.${userId}`;
}

function loadCache(orgId: string, userId: string): EncounterTabSettings | null {
  try {
    const raw = localStorage.getItem(cacheKey(orgId, userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.version !== 1 || !Array.isArray(parsed.tabs)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeCache(orgId: string, userId: string, settings: EncounterTabSettings): void {
  try {
    localStorage.setItem(cacheKey(orgId, userId), JSON.stringify(settings));
  } catch {
    // ignore
  }
}

// ─── Hook ───

export function useEncounterTabConfig(orgId: string, userId: string) {
  const { db } = usePowerSyncDb();

  // Query PowerSync for this user's settings
  const { data: dbRows = [] } = usePowerSyncQuery<EncounterTabSettingsRow>({
    key: ['encounter_tab_settings', orgId, userId],
    sql: `SELECT * FROM encounter_tab_settings WHERE org_id = ? AND user_id = ? LIMIT 1`,
    params: [orgId, userId],
    watch: ['encounter_tab_settings'],
  });

  // Initialize from localStorage cache (fast startup), then sync from DB
  const [settings, setSettings] = useState<EncounterTabSettings>(() => {
    const cached = loadCache(orgId, userId);
    if (cached) {
      return { ...cached, tabs: reconcileTabConfig(DEFAULT_TABS, cached.tabs) };
    }
    return { version: 1, preset: null, tabs: [...DEFAULT_TABS] };
  });

  // Sync from DB when PowerSync data arrives (preserve layout from local state)
  const dbRow = dbRows[0] ?? null;
  useEffect(() => {
    if (!dbRow) return;
    try {
      const tabs = JSON.parse(dbRow.tabs_json) as TabConfig[];
      setSettings((prev) => {
        const fromDb: EncounterTabSettings = {
          version: 1,
          preset: dbRow.preset,
          tabs: reconcileTabConfig(DEFAULT_TABS, tabs),
          layout: prev.layout, // preserve layout from local state / localStorage
        };
        writeCache(orgId, userId, fromDb);
        return fromDb;
      });
    } catch {
      // ignore bad data
    }
  }, [dbRow?.id, dbRow?.updated_at]);

  const visibleTabs = useMemo(() => settings.tabs.filter((t) => t.visible), [settings.tabs]);

  const updateSettings = useCallback(
    async (next: EncounterTabSettings) => {
      // Apply immediately
      setSettings(next);
      writeCache(orgId, userId, next);

      // Persist to PowerSync
      if (!db) return;
      const tabsJson = JSON.stringify(next.tabs);
      const existingId = dbRow?.id;

      if (existingId) {
        await db.execute(
          `UPDATE encounter_tab_settings SET preset = ?, tabs_json = ?, updated_at = datetime('now') WHERE id = ?`,
          [next.preset ?? null, tabsJson, existingId],
        );
      } else {
        const id = crypto.randomUUID();
        await db.execute(
          `INSERT INTO encounter_tab_settings (id, org_id, user_id, preset, tabs_json, created_at, updated_at) VALUES (?, ?, ?, ?, ?, datetime('now'), datetime('now'))`,
          [id, orgId, userId, next.preset ?? null, tabsJson],
        );
      }
    },
    [db, orgId, userId, dbRow?.id],
  );

  const applyPreset = useCallback(
    (presetKey: string) => {
      const preset = PRESETS[presetKey];
      if (!preset) return;
      const next: EncounterTabSettings = {
        version: 1,
        preset: presetKey,
        tabs: reconcileTabConfig(DEFAULT_TABS, preset.tabs),
      };
      updateSettings(next);
    },
    [updateSettings],
  );

  const resetToDefault = useCallback(() => {
    const next: EncounterTabSettings = {
      version: 1,
      preset: null,
      tabs: [...DEFAULT_TABS],
      layout: { ...DEFAULT_LAYOUT },
    };
    updateSettings(next);
  }, [updateSettings]);

  const layoutConfig = useMemo<LayoutConfig>(
    () => settings.layout ?? DEFAULT_LAYOUT,
    [settings.layout],
  );

  return {
    settings,
    visibleTabs,
    layoutConfig,
    updateSettings,
    applyPreset,
    resetToDefault,
  };
}
